import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box, Chip, Typography, Alert } from "@mui/material";
import {
    Description as DescriptionIcon, CheckCircle as CheckCircleIcon, Cancel as CancelIcon,
    Warning as WarningIcon
} from "@mui/icons-material";
import Papa from 'papaparse';
import API from '@/utils/axios';
import MovingArrows from "@components/mailinglist/MovingArrows";


function MailingListImport({ formData, file, encoding, buttons, columns, setNextDisabled, setDialogHeight, onImportDone }) {
    const { t } = useTranslation();
    const [importInProgress, setImportInProgress] = useState(true);
    const [nbImported, setNbImported] = useState(0);
    const [nbProcessed, setNbProcessed] = useState(0);
    const [errors, setErrors] = useState([]);
    const [warnings, setWarnings] = useState([]);
    const [globalError, setGlobalError] = useState(false);
    const [mailingListId, setMailingListId] = useState(null);

    useEffect(() => {
        setNextDisabled(true);
    }, [setNextDisabled]);

    useEffect(() => {
        if (!importInProgress) {
            return;
        }

        // 1/ Création de la mailing list
        API.post('/mailing-lists', formData)
            .then((response) => {

                // 2/ Import des contacts dans la mailing list,
                //    envoi par paquets de 100 contacts
                const mailingListId = response.data.id;
                setMailingListId(mailingListId);
                const sender = chunckSender({
                    chunkSize: 100, mailingListId, onFinished: (nbSuccess, errors, warnings) => {
                        // Tri des erreurs et warnings par numéro de ligne
                        errors.sort((a, b) => a.line - b.line);
                        warnings.sort((a, b) => a.line - b.line);

                        setDialogHeight('auto');
                        setImportInProgress(false);
                        setNbImported(nbSuccess);
                        setErrors(errors);
                        setWarnings(warnings);
                        setNextDisabled(false);
                        if (nbSuccess > 0) {
                            onImportDone();
                        } else {
                            setGlobalError(true);
                        }
                    },
                    onProgress: (nbProcessed) => {
                        setNbProcessed(nbProcessed);
                    },
                });

                Papa.parse(file, {
                    header: true,
                    encoding: encoding,
                    skipEmptyLines: true,
                    worker: true,
                    step: (results) => {

                        if (results.data) {
                            const formattedData = {
                                lang: null,
                                gender: null,
                                first_name: null,
                                last_name: null,
                                email: null,
                                phone: null,
                                extra: {},
                            };
                            Object.entries(results.data).forEach(([key, value]) => {
                                const column = columns[key];
                                if (column === 'ignore') {
                                    return;
                                } else if (column === 'extra') {
                                    formattedData.extra[key] = value;
                                } else {
                                    formattedData[column] = value;
                                }
                            });

                            sender.add(formattedData);
                        }
                    },
                    complete: () => {
                        sender.end();
                    },
                    error: (error) => {
                        console.error(error);
                    }
                });
            })
            .catch((error) => {
                setErrors([{ line: 0, message: 'Failed to create mailing list' }]);
                setGlobalError(true);
                setDialogHeight('auto');
                setImportInProgress(false);
                setNbImported(0);
                setNbProcessed(0);
                setNextDisabled(false);
            });

    }, [importInProgress, formData, file, encoding, columns, setDialogHeight,
        setNextDisabled, onImportDone, setGlobalError]);

    // 3/ Suppression de la mailing list en cas d'erreur
    useEffect(() => {
        if (mailingListId && globalError) {
            API.delete(`/mailing-lists/${mailingListId}`);
        }
    }, [mailingListId, globalError]);


    return (
        <div>
            <Box sx={{ mb: 5, textAlign: 'center' }}>
                {file && (
                    <Chip icon={<DescriptionIcon />} label={file.name} variant="outlined" sx={{ mb: 1 }} />
                )}
                {importInProgress ? (
                    <div>
                        <MovingArrows />
                        <Typography variant="h4">
                            {t('Import in progress')}...
                            &nbsp;
                            {nbProcessed > 0 ? t('Do not leave this page until the end of the import.') : ''}
                        </Typography>
                        {nbProcessed > 0 && (
                            <Typography variant="h6">{t('{{nb}} contacts processed', { nb: nbProcessed })}</Typography>
                        )}
                    </div>
                ) : (
                    <div>
                        {globalError ? (
                            <div>
                                <CancelIcon color="error" sx={{ fontSize: 50 }} />
                                <Typography variant="h4">
                                    {t('Import error')}
                                </Typography>
                                <Typography variant="h6">
                                    {t('No contact imported. The mailing list has not been created.')}
                                </Typography>
                            </div>
                        ) : (
                            <div>
                                {errors.length > 0 ? (
                                    <WarningIcon color="error" sx={{ fontSize: 50 }} />
                                ) : warnings.length > 0 ? (
                                    <WarningIcon color="warning" sx={{ fontSize: 50 }} />
                                ) : (
                                    <CheckCircleIcon color="success" sx={{ fontSize: 50 }} />
                                )}
                                <Typography variant="h4">
                                    {t('Import done')}
                                </Typography>
                                <Typography variant="h6">
                                    {nbProcessed === nbImported ? (
                                        t('{{nbImported}} contacts imported with success', { nbImported })
                                    ) : (
                                        t('{{nbProcessed}} contacts processed, {{nbImported}} imported with success',
                                            { nbImported, nbProcessed })
                                    )}
                                </Typography>
                            </div>
                        )}
                        {errors.length > 0 && (
                            <Alert severity="error" sx={{ mt: 2, textAlign: 'left' }}>
                                {errors.map((item, index) => (
                                    item.line > 0 ? (
                                        <div>
                                            {t('Line {{line}}: {{message}}', { line: item.line, message: t(item.message) })}
                                            &nbsp;
                                            {t('This line has not been imported.')}
                                        </div>
                                    ) : (
                                        t(item.message)
                                    )
                                )
                                )}
                            </Alert>
                        )}
                        {warnings.length > 0 && (
                            <Alert severity="warning" sx={{ mt: 2, textAlign: 'left' }}>
                                {warnings.map((item, index) => (
                                    <div>
                                        {t('Line {{line}}: {{message}}', {
                                            line: item.line,
                                            message: t(item.message) + ' : "' + item.value + '".'
                                                + (item.message2 ? ' ' + t(item.message2) : '')
                                        })}
                                    </div>
                                ))}
                            </Alert>
                        )}
                    </div>
                )}
            </Box>

            {buttons}
        </div>
    );
}


function chunckSender({ chunkSize, mailingListId, onProgress, onFinished }) {
    let data = [];
    let nbCalls = 0;
    let nbCallsDone = 0;
    let nbSuccess = 0;   // Nombre de contacts importés avec succès
    let nbProcessed = 0; // Nombre de contacts traités, en succès ou en erreur
    let errors = [];
    let warnings = [];
    let activeRequests = 0;
    const maxSimultaneousRequests = 2;
    let currentLineNumber = 0; // Pour suivre le numéro de ligne réel dans le fichier

    function add(row) {
        currentLineNumber++; // Incrémenter le numéro de ligne à chaque ajout
        data.push({ ...row, _lineNumber: currentLineNumber }); // Ajouter le numéro de ligne à chaque contact
        if (data.length >= chunkSize && activeRequests < maxSimultaneousRequests) {
            send();
        }
    }

    function end() {
        if (activeRequests < maxSimultaneousRequests) {
            send();
        }
    }

    function send() {
        if (activeRequests >= maxSimultaneousRequests) return;

        activeRequests++;
        nbCalls++;

        if (data.length === 0) {
            nbCallsDone++;
            activeRequests--;
            if (nbCalls === nbCallsDone) {
                onFinished(nbSuccess, errors, warnings);
            }
            return;
        }

        const payload = data.slice(0, chunkSize);
        data = data.slice(chunkSize);

        // Créer un mapping des numéros de ligne pour ce paquet
        const lineNumberMapping = payload.reduce((acc, item, index) => {
            acc[index + 1] = item._lineNumber; // +1 car le serveur commence à 1
            return acc;
        }, {});

        // Supprimer les _lineNumber avant l'envoi
        const cleanPayload = payload.map(({ _lineNumber, ...rest }) => rest);

        API.post(`/mailing-lists/${mailingListId}/contacts`, cleanPayload)
            .then((response) => {
                if (response.data.success) {
                    nbSuccess += response.data.ids.length;
                    nbProcessed += payload.length;
                    onProgress(nbProcessed);
                }
                if (response.data.warnings) {
                    // Convertir les numéros de ligne relatifs en numéros de ligne réels
                    const mappedWarnings = response.data.warnings.map(warning => ({
                        ...warning,
                        line: lineNumberMapping[warning.line] || warning.line
                    }));
                    warnings = [...warnings, ...mappedWarnings];
                }
                if (response.data.errors) {
                    // Convertir les numéros de ligne relatifs en numéros de ligne réels
                    const mappedErrors = response.data.errors.map(error => ({
                        ...error,
                        line: lineNumberMapping[error.line] || error.line
                    }));
                    errors = [...errors, ...mappedErrors];
                }
            })
            .catch((error) => {
                errors.push(error);
            })
            .finally(() => {
                nbCallsDone++;
                activeRequests--;
                if (data.length > 0 && activeRequests < maxSimultaneousRequests) {
                    send();
                }
                if (nbCalls === nbCallsDone) {
                    onFinished(nbSuccess, errors, warnings);
                }
            });
    }

    return { add, end };
}

export default MailingListImport;